import * as React from "react";
import { TableCell, TableRow } from "@mui/material";
import { ISearchparamUI, ITableUI } from "./TableTemplate";
import {
  Header,
  ISearchParameters,
  ITableHeaderState,
  ITableReport,
  Report,
  TableHeaderState,
} from "src/Interfaces/TableInterfaces";
import CheckBox from "src/Components/Checkbox";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import Cell, { unCapitalizeFirstLetter } from "src/Tables/TableTemplate/Cell";
import Button from "src/Components/Button";
import { Edit, Timeline } from "@mui/icons-material";
import { Modal, StaticDropdown, ToggleSwitch } from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Local Components
import Table from "./Table";
// import SortIcon from "@mui/icons-material/Sort";

export const CommonSearchParam: React.FC<ISearchparamUI<ISearchParameters>> = ({
  intialValue,
  onSubmit,
}: ISearchparamUI<ISearchParameters>) => {
  return <></>;
};

const headerCellClassName =
  "!cursor-default bg-white !font-black text-black !text-center";

const groupDynamicHeaders = (headers: Header[]) => {
  const groups: { groupName: string; headers: Header[] }[] = [];

  headers.forEach((header) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.groupName === header.groupName) {
      lastGroup.headers.push(header);
      return;
    }

    groups.push({ groupName: header.groupName!, headers: [header] });
  });

  return groups;
};

const filterDynamicHeaders = (headers: Header[], showHiddenCols?: boolean) => {
  if (showHiddenCols == null) return headers;

  return headers.filter((header) =>
    showHiddenCols
      ? header.positionTypeStr === "FirstPage" ||
        header.positionTypeStr === "DefaultHidden"
      : header.positionTypeStr === "FirstPage",
  );
};

export const CommonTableUI = <TRow extends ITableReport>({
  reports,
  AdditionalBody,
  onAction,
  tableHeaderClassName,
  tableBoxClassName,
  withSelect,
  onTableRowClick,
  onTableHeaderClick,
  onBulkCancelClick,
  sortingType,
  setSortingType,
  showHiddenCols,
  getExtraTypeUI,
  hideWhenNoResp,
  filterCols,
  refreshFlag,
  RightSideAdditionalBody,
  activeRowId,
  onSelectedRowsChange,
  scrollKey = "#",
  type = "aggregate",
}: ITableUI<TRow>) => {
  const headerStateLocalStorageKey =
    "tableHeaderStates" + filterCols?.tableName;

  const [selectedRowsIds, setSelectedRowsIds] = React.useState<number[]>([]);
  const [
    isTableHeaderCustomizationModalOpen,
    setIsTableHeaderCustomizationModalOpen,
  ] = React.useState<boolean>(false);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const [tableHeaderStates, setTableHeaderStates] = React.useState(
    {} as ITableHeaderState,
  );
  const firstHeaderRowRef = React.useRef<HTMLTableRowElement>(null);
  const [firstRowHeight, setFirstRowHeight] = React.useState(0);

  React.useEffect(() => {
    const storedScroll = sessionStorage.getItem(scrollKey);
    if (storedScroll && scrollRef.current) {
      const { x, y } = JSON.parse(storedScroll);
      scrollRef.current.scrollLeft = x;
      scrollRef.current.scrollTop = y;
    }
  }, [scrollKey]);
  React.useEffect(() => {
    if (filterCols && filterCols.show && !Object.keys(tableHeaderStates).length)
      getTableHeaderStates();
  }, [reports]);

  React.useLayoutEffect(() => {
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const updateHeight = () => {
      setFirstRowHeight(row.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  function getTableHeaderStates() {
    let result = {} as ITableHeaderState;

    if (localStorage.getItem(headerStateLocalStorageKey)) {
      const storedState = JSON.parse(
        localStorage.getItem(headerStateLocalStorageKey)!,
      ) as ITableHeaderState;
      result = { ...result, ...storedState };
    }

    setTableHeaderStates(result);
  }

  const handleScroll = () => {
    if (scrollRef.current) {
      sessionStorage.setItem(
        scrollKey,
        JSON.stringify({
          x: scrollRef.current.scrollLeft,
          y: scrollRef.current.scrollTop,
        }),
      );
    }
  };

  const isRowSelected = (id: number) => {
    if (!selectedRowsIds) return false;
    const res = selectedRowsIds.find((i) => i === id);
    if (res) return true;
    return false;
  };

  React.useEffect(() => {
    if (refreshFlag && onAction) onAction();
  }, [refreshFlag]);

  const selectCheckBoxChangeHandler = (value: boolean, rowId: number) => {
    if (!selectedRowsIds) {
      setSelectedRowsIds([]);
      if (onSelectedRowsChange) onSelectedRowsChange([]);
    }
    if (value) {
      setSelectedRowsIds([...selectedRowsIds, rowId]);
      if (onSelectedRowsChange)
        onSelectedRowsChange([...selectedRowsIds, rowId]);
    }
    if (!value) {
      const res = selectedRowsIds?.filter((id) => id !== rowId);
      if (res) {
        setSelectedRowsIds(res);
        if (onSelectedRowsChange) onSelectedRowsChange(res);
      } else {
        setSelectedRowsIds(selectedRowsIds);
        if (onSelectedRowsChange) onSelectedRowsChange(selectedRowsIds || []);
      }
    }
  };

  const getFixedFirstCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders.filter(
      (header) =>
        header.positionTypeStr === "FixedFirst" && !header.groupName,
    ),
  });

  const getFilteredCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders
      .map((theader) => ({
        ...theader,
        positionTypeStr: Object.keys(tableHeaderStates).length
          ? tableHeaderStates[theader.headerName] !== TableHeaderState.Auto
            ? tableHeaderStates[theader.headerName] || theader.positionTypeStr
            : theader.positionTypeStr
          : theader.positionTypeStr,
      }))
      .filter(
        (f) =>
          !f.groupName &&
          f.positionTypeStr !== TableHeaderState.Hidden &&
          [...(filterCols?.allowedPositions || []), "FirstPage"].includes(
            f.positionTypeStr || "",
          ),
      )
      .map((h) => ({
        ...h,
        classname:
          h.positionTypeStr === "DefaultHidden"
            ? "!bg-sky-200"
            : h.positionTypeStr === "Mediator"
              ? "!bg-red-200"
              : "",
      })),
  });

  const getMiddleCols = (): Report<TRow> =>
    filterCols
      ? getFilteredCols()
      : {
          ...reports,
          translatedHeaders: reports.translatedHeaders.filter(
            (header) =>
              !header.groupName &&
              (showHiddenCols
                ? header.positionTypeStr === "FirstPage" ||
                  header.positionTypeStr === "DefaultHidden"
                : header.positionTypeStr === "FirstPage"),
          ),
        };
  const getFixedLastCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders.filter(
      (header) => header.positionTypeStr === "FixedLast" && !header.groupName,
    ),
  });

  const getDynamicHeaders = (): Header[] =>
    filterDynamicHeaders(
      reports.translatedHeaders.filter((header) => header.groupName),
      showHiddenCols,
    );

  const getTableCellJSX = (
    theader: Header,
    clonedRow: any,
    className?: string,
  ) => {
    const cName =
      clonedRow[unCapitalizeFirstLetter(theader.headerName) + "Classname"];
    return (
      <TableCell
        key={theader.headerName + clonedRow.id}
        className={cName + " " + className}
      >
        <div className="flex flex-row justify-center items-center gap-2 !w-full">
          {getExtraTypeUI ? (
            theader.extraTypeStr === "NoExtra" ? (
              <Cell
                onAction={onAction}
                clonedRow={clonedRow}
                headerName={theader.headerName}
                type={theader.typeStr}
              />
            ) : (
              getExtraTypeUI({
                row: clonedRow,
                extraType: theader.extraTypeStr,
                headerName: theader.headerName,
                cellType: theader.typeStr || "",
              })
            )
          ) : (
            <Cell
              onAction={onAction}
              clonedRow={clonedRow}
              headerName={theader.headerName}
              type={theader.typeStr}
            />
          )}
        </div>
      </TableCell>
    );
  };

  const getPairSeparatorClassName = (index: number, total: number) => {
    const colNumberFromLeft = total - index;
    const isLastTwoColumnsFromLeft = colNumberFromLeft <= 2;
    const isStartOfNewPair = colNumberFromLeft % 2 === 1;

    if (index === 0) return "!border-r-2 !border-r-sky-200 ";
    if (isLastTwoColumnsFromLeft || !isStartOfNewPair) return "";
    return "!border-l-2 !border-l-sky-200";
  };

  const GetRow = (_: Report<TRow>, row: TRow) => {
    const clonedRow = JSON.parse(JSON.stringify(row));
    const dynamicHeaders = getDynamicHeaders();
    const fixedFirst = getFixedFirstCols().translatedHeaders.map((theader) =>
      getTableCellJSX(
        theader,
        clonedRow,
        `!z-10 !bg-sky-50 !border-0 ${
          theader.translateHeaderName === "#" ? "" : "inner-fixed-column"
        }`,
      ),
    );
    const middleHeaders = getMiddleCols().translatedHeaders;
    const middle = middleHeaders.map((theader, index) => {
      return getTableCellJSX(
        theader,
        clonedRow,
        getPairSeparatorClassName(index, middleHeaders.length),
      );
    });
    const dynamic = dynamicHeaders.map((theader, index) =>
      getTableCellJSX(
        theader,
        clonedRow,
        getPairSeparatorClassName(index, dynamicHeaders.length),
      ),
    );
    const fixedLast = getFixedLastCols().translatedHeaders.map((theader) => {
      return getTableCellJSX(
        theader,
        clonedRow,
        `sticky left-0 !z-10 !bg-sky-50 !border-0 ${
          theader.translateHeaderName === "#" ? "" : "inner-fixed-column"
        }`,
      );
    });

    return (
      <>
        {RightSideAdditionalBody && onAction ? (
          <RightSideAdditionalBody.getRow
            row={row}
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
          />
        ) : (
          <></>
        )}
        {getFixedFirstCols().translatedHeaders.length ? (
          <TableCell className="!sticky !right-0 !z-50 !p-0 fixed-column bg-sky-50">
            {fixedFirst}
          </TableCell>
        ) : (
          <></>
        )}

        {middle}
        {dynamic}
        {getFixedLastCols().translatedHeaders.length ? (
          <TableCell className="!sticky !left-0 !z-50 fixed-column">
            {fixedLast}
          </TableCell>
        ) : (
          <></>
        )}
        {AdditionalBody && onAction ? (
          <AdditionalBody.getRow
            row={row}
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
          />
        ) : (
          <></>
        )}
      </>
    );
  };

  const isAllRowsSelected = () => {
    if (!selectedRowsIds) return false;
    if (!selectedRowsIds) return false;
    if (reports.report?.length === selectedRowsIds.length) return true;
    return false;
  };

  const onSelectAllChange = (value: boolean) => {
    if (!value) setSelectedRowsIds([]);
    else {
      const res = reports.report.map((report) => report.id);
      setSelectedRowsIds(res);
    }
  };

  const tableHeaderClickHandler = (header: Header) => {
    if ((sortingType || sortingType === "") && setSortingType)
      setSortingType(header.headerName);
    if (onTableHeaderClick) onTableHeaderClick(header);
  };

  const getHeadersSortingIcon = (theader: Header) => {
    if (!sortingType) return null;

    const sortingTypeParts = sortingType.split("_");
    const sortingTypeHeaderName = sortingTypeParts.slice(0, -1).join("_"); // gets every part except last ones
    if (theader.headerName === sortingTypeHeaderName) {
      if (sortingTypeParts[sortingTypeParts.length - 1] === "desc")
        return <SouthIcon className="!text-xs" />;
      if (sortingTypeParts[sortingTypeParts.length - 1] === "asc")
        return <NorthIcon className="!text-xs" />;
    }
  };

  const getTableHeaderJSX = (
    theader: Header,
    className?: string,
    rowSpan?: number,
  ) => (
    <TableCell
      key={theader.headerName}
      rowSpan={rowSpan}
      className={`!cursor-pointer bg-white hover:!bg-slate-200 !transition-all ${theader.classname} ${className} ${tableHeaderClassName}`}
      onClick={() => tableHeaderClickHandler(theader)}
    >
      {theader.translateHeaderName}
      {theader.extraTypeStr === "WithChart" ? (
        <Timeline fontSize="small" className="!text-gray-700" />
      ) : (
        <></>
      )}
      {getHeadersSortingIcon(theader)}
    </TableCell>
  );

  const header = () => {
    const dynamicHeaders = getDynamicHeaders();
    const dynamicGroups = groupDynamicHeaders(dynamicHeaders);
    const hasDynamicHeaders = dynamicHeaders.length > 0;
    const staticHeaderRowSpan = hasDynamicHeaders ? 2 : undefined;

    const staticHeaderCells = (
      <>
        {withSelect ? (
          <TableCell
            key="selectRowCheckBox"
            rowSpan={staticHeaderRowSpan}
            className="!p-3"
            onClick={(e) => e.stopPropagation()}
          >
            {reports && reports.report?.length ? (
              <CheckBox
                checked={isAllRowsSelected()}
                label=""
                name=""
                onChange={(name, value) => onSelectAllChange(value)}
              />
            ) : null}
          </TableCell>
        ) : null}
        {reports &&
        reports.translatedHeaders &&
        getFixedFirstCols().translatedHeaders.length ? (
          <TableCell
            rowSpan={staticHeaderRowSpan}
            className="!sticky !right-0 !z-[100] fixed-column"
          >
            {getFixedFirstCols().translatedHeaders.map((theader) => {
              return getTableHeaderJSX(
                theader,
                `!bg-sky-50 !border-0 !flex-1 ${
                  theader.translateHeaderName === "#"
                    ? ""
                    : "inner-fixed-column"
                }`,
              );
            })}
          </TableCell>
        ) : (
          <></>
        )}
        {reports &&
          reports.translatedHeaders &&
          getMiddleCols().translatedHeaders.map((theader, index, headers) => {
            return getTableHeaderJSX(
              theader,
              getPairSeparatorClassName(index, headers.length),
              staticHeaderRowSpan,
            );
          })}
        {reports &&
        reports.translatedHeaders &&
        getFixedLastCols().translatedHeaders.length ? (
          <TableCell
            rowSpan={staticHeaderRowSpan}
            className="!sticky !left-0 !z-100 fixed-column"
          >
            {getFixedLastCols().translatedHeaders.map((theader) => {
              return getTableHeaderJSX(theader, "!bg-sky-50 !border-0 !flex-1");
            })}
          </TableCell>
        ) : (
          <></>
        )}
      </>
    );

    const additionalHeaderCells =
      AdditionalBody && onAction ? (
        <AdditionalBody.getHeader
          onAction={onAction}
          selectedRowsIds={selectedRowsIds}
          setSelectedRowsIds={setSelectedRowsIds}
          isSelectAll={
            selectedRowsIds && reports
              ? selectedRowsIds.length === reports.report.length
              : false
          }
        />
      ) : null;

    const rightSideAdditionalHeaderCells =
      RightSideAdditionalBody && onAction ? (
        <RightSideAdditionalBody.getHeader
          onAction={onAction}
          selectedRowsIds={selectedRowsIds}
          setSelectedRowsIds={setSelectedRowsIds}
          isSelectAll={
            selectedRowsIds && reports
              ? selectedRowsIds.length === reports.report.length
              : false
          }
        />
      ) : null;

    if (!hasDynamicHeaders) {
      if (RightSideAdditionalBody && onAction) {
        return (
          <TableRow>
            {rightSideAdditionalHeaderCells}
            {staticHeaderCells}
            {additionalHeaderCells}
          </TableRow>
        );
      }

      return (
        <TableRow>
          {staticHeaderCells}
          {additionalHeaderCells}
        </TableRow>
      );
    }

    const groupedHeaderCells = dynamicGroups.map((group, index) => (
      <TableCell
        key={group.groupName}
        colSpan={group.headers.length}
        className={`${headerCellClassName} ${index === 0 ? "!border-r-2 !border-r-sky-200 !border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""} ${index > 0 ? "!border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""}`}
      >
        {group.groupName}
      </TableCell>
    ));

    const dynamicHeaderCells = dynamicHeaders.map((theader, index) =>
      getTableHeaderJSX(
        theader,
        getPairSeparatorClassName(index, dynamicHeaders.length),
      ),
    );

    if (RightSideAdditionalBody && onAction) {
      return (
        <>
          <TableRow ref={firstHeaderRowRef}>
            {rightSideAdditionalHeaderCells}
            {staticHeaderCells}
            {groupedHeaderCells}
            {additionalHeaderCells}
          </TableRow>
          <TableRow>{dynamicHeaderCells}</TableRow>
        </>
      );
    }

    return (
      <>
        <TableRow ref={firstHeaderRowRef}>
          {staticHeaderCells}
          {groupedHeaderCells}
          {additionalHeaderCells}
        </TableRow>
        <TableRow>{dynamicHeaderCells}</TableRow>
      </>
    );
  };

  const dynamicHeaders = reports ? getDynamicHeaders() : [];
  const multiRowStickyHeaderSx = dynamicHeaders.length
    ? {
        "& .MuiTableCell-head": {
          backgroundColor: "#fff",
        },
        "& thead tr:first-of-type .MuiTableCell-head": {
          top: 0,
          zIndex: 3,
        },
        "& thead tr:nth-of-type(2) .MuiTableCell-head": {
          top: firstRowHeight,
          zIndex: 2,
        },
      }
    : undefined;

  const tableRowClickHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: TRow,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (onTableRowClick) onTableRowClick(row);
  };

  const body = (
    <>
      {reports && reports.translatedHeaders && reports.report.length ? (
        reports.report.map((row) => {
          return (
            <TableRow
              key={row.row}
              selected={isRowSelected(row.id)}
              className={`hover:!bg-slate-200 !transition-all cursor-pointer !w-full !relative ${
                activeRowId && activeRowId === row.id ? "!bg-blue-100" : ""
              }`}
              onClick={(e) => tableRowClickHandler(e, row)}
            >
              {withSelect ? (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <CheckBox
                    checked={!!isRowSelected(row.id)}
                    label=""
                    name=""
                    onChange={(name, value) =>
                      selectCheckBoxChangeHandler(value, row.id)
                    }
                  />
                </TableCell>
              ) : null}
              {GetRow(reports, row)}
            </TableRow>
          );
        })
      ) : (
        <TableRow className="!relative h-10 !w-full">
          <TableCell className="!w-full !m-auto !flex !justify-center !items-center !h-full !absolute">
            موردی یافت نشد
          </TableCell>
        </TableRow>
      )}
    </>
  );

  const tableHeaderStateOptions: DropdownItem[] = [
    { name: "مخفی", id: TableHeaderState.Hidden },
    { name: "جزئیات", id: TableHeaderState.DefaultHidden },
    { name: "اتوماتیک", id: TableHeaderState.Auto },
    { name: "محاسباتی", id: TableHeaderState.Mediator },
    { name: "صفحه اصلی", id: TableHeaderState.FirstPage },
  ];

  const handleTableHeaderStateChange = (
    headerName: string,
    state: TableHeaderState,
  ) => {
    setTableHeaderStates((prev) => ({
      ...prev,
      [headerName]: state,
    }));
  };

  const handleSubmitHeaderStates = () => {
    localStorage.setItem(
      headerStateLocalStorageKey,
      JSON.stringify(tableHeaderStates),
    );
    setIsTableHeaderCustomizationModalOpen(false);
  };

  const tableHeaderCustomizationUI = (
    <div className="flex flex-col justify-start items-start gap-8">
      {reports &&
      reports.translatedHeaders &&
      reports.translatedHeaders.length ? (
        <div className="flex flex-col justify-start items-start gap-3 max-h-[400px] overflow-y-auto w-full">
          {reports.translatedHeaders
            .filter(
              (header) =>
                !header.groupName &&
                header.positionTypeStr !== "FixedFirst" &&
                header.positionTypeStr !== "FixedLast",
            )
            .map((header) => (
              <div
                key={header.headerName}
                className="flex justify-start items-center w-full gap-5"
              >
                <div className="mr-2 whitespace-nowrap">
                  {header.translateHeaderName}
                </div>
                <div className="mr-auto w-40">
                  <StaticDropdown
                    options={tableHeaderStateOptions}
                    onChange={(_, value) =>
                      handleTableHeaderStateChange(header.headerName, value)
                    }
                    dropdown={{
                      name: "",
                      placeholder: "وضعیت",
                    }}
                    value={
                      tableHeaderStates[header.headerName] ||
                      TableHeaderState.Auto
                    }
                  />
                </div>
              </div>
            ))}
        </div>
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-start items-center gap-3 w-full">
        <Button
          text="پیش فرض"
          onClick={() => {
            localStorage.removeItem(headerStateLocalStorageKey);
            setTableHeaderStates({} as ITableHeaderState);
            setIsTableHeaderCustomizationModalOpen(false);
          }}
          variant="outlined"
        />

        <Button
          text="ذخیره تغییرات"
          onClick={handleSubmitHeaderStates}
          variant="contained"
        />
      </div>
    </div>
  );

  if (!reports) return null;
  if (!reports.count && hideWhenNoResp) return <></>;
  return (
    <div className="w-full flex flex-col justify-start items-end gap-5">
      {isTableHeaderCustomizationModalOpen ? (
        <Modal
          UI={tableHeaderCustomizationUI}
          onClose={() => setIsTableHeaderCustomizationModalOpen(false)}
        />
      ) : (
        <></>
      )}
      {filterCols && filterCols.show ? (
        <div
          className="absolute -top-2 -right-2 bg-sky-600 w-6 h-6 !text-white rounded-full flex justify-center items-center z-[101] cursor-pointer"
          onClick={() => setIsTableHeaderCustomizationModalOpen(true)}
        >
          <Edit
            onClick={() => setIsTableHeaderCustomizationModalOpen(true)}
            fontSize="small"
          />
        </div>
      ) : (
        <></>
      )}
      <div
        className={`w-full m-auto border-2 border-sky-100 rounded overflow-x-auto ${tableBoxClassName}`}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        <Table
          header={header()}
          body={body}
          type={type}
          tableSx={multiRowStickyHeaderSx}
        />
      </div>
      {withSelect && onBulkCancelClick ? (
        <Button
          onClick={() => {
            if (onBulkCancelClick) onBulkCancelClick(selectedRowsIds);
          }}
          text="حذف گروهی"
          variant="contained"
          disabled={!!!selectedRowsIds.length}
          className={`${!!!selectedRowsIds.length ? "" : "red-btn"} max-w-min`}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
